import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Grid } from '@mui/material';
import {
  Business as BusinessIcon,
  Apps as AppsIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  CheckCircle,
  Block,
  ErrorOutline,
} from '@mui/icons-material';
import KpiGrid from '../dashboard/shared/KpiGrid';
import AuthAnalyticsSection from '../dashboard/shared/AuthAnalyticsSection';
import DeviceAnalyticsSection from '../dashboard/shared/DeviceAnalyticsSection';
import BusinessRankingSection from '../dashboard/admin/BusinessRankingSection';
import ApplicationsSection from '../dashboard/admin/ApplicationsSection';
import SecuritySection from '../dashboard/admin/SecuritySection';
import { adminDashboardService } from '../../services/dashboard';
import { useAuth } from '../../context/AuthContext';

const overviewKpis = [
  {
    key: 'totalBusinesses',
    title: 'Businesses',
    icon: <BusinessIcon />,
    format: (v) => v.value.toLocaleString(),
    subtitle: (data) => (data.totalBusinesses.newInWindow ? `+${data.totalBusinesses.newInWindow} this month` : undefined),
    tooltip: 'Total number of businesses registered on the platform. A business is an entity that can have multiple applications and users.',
  },
  {
    key: 'totalApplications',
    title: 'Applications',
    icon: <AppsIcon />,
    format: (v) => v.value.toLocaleString(),
    subtitle: (data) => (data.totalApplications.newInWindow ? `+${data.totalApplications.newInWindow} this month` : undefined),
    tooltip: 'Total number of applications registered on the platform.',
  },
  {
    key: 'totalSubscribers',
    title: 'Total Subscribers',
    icon: <PeopleIcon />,
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Total number of subscribers on the platform.',
  },
  {
    key: 'activeSubscribers',
    title: 'Active Subscribers',
    icon: <PeopleIcon />,
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Subscribers who have made at least one authentication transaction in the last 30 days.',
  },
  {
    key: 'activeDevices',
    title: 'Active Devices',
    icon: <DevicesIcon />,
    format: (v) => v.value.toLocaleString(),
  },
  {
    key: 'totalAuthTransactions',
    title: 'Authentication Requests',
    icon: <CheckCircle />,
    format: (v) => v.value.toLocaleString(),
    trend: (data) => (data.totalAuthTransactions.changePct >= 0 ? 'up' : 'down'),
    trendValue: (data) => `${Math.abs(data.totalAuthTransactions.changePct)}%`,
  },
  {
    key: 'successRate',
    title: 'Success Rate',
    icon: <CheckCircle />,
    format: (v) => `${v.value}%`,
    trend: (data) => (data.successRate.changePct >= 0 ? 'up' : 'down'),
    trendValue: (data) => `${Math.abs(data.successRate.changePct)}%`,
  },
  {
    key: 'blockedUsers',
    title: 'Blocked Subscribers',
    icon: <Block />,
    format: (v) => v.value.toLocaleString(),
    subtitle: (data) => (data.blockedUsers.newInWindow ? `+${data.blockedUsers.newInWindow} this month` : undefined),
    tooltip: 'Total number of blocked subscribers on the platform.',
  },
];

// const TABS = ['Authentication', 'Businesses', 'Applications', 'Devices', 'Security'];

const TABS = ['Authentication', 'Businesses', 'Applications', 'Devices'];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [tab, setTab] = useState(0);
  const [reloadKey] = useState(0);

  React.useEffect(() => {
    adminDashboardService
      .getOverview(30)
      .then((res) => setOverview(res.data))
      .catch((e) => setOverviewError(e.message))
      .finally(() => setOverviewLoading(false));
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Platform Admin Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {user?.email ? `Signed in as ${user.email}` : 'Platform-wide health & usage'}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <KpiGrid items={overviewKpis} data={overview} loading={overviewLoading} error={overviewError} />
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {tab === 0 && (
            <AuthAnalyticsSection
              fetchVolume={(period) => adminDashboardService.getAuthenticationVolume(period)}
              fetchMethods={(period) => adminDashboardService.getAuthenticationMethods(period)}
              fetchStatus={(period) => adminDashboardService.getAuthenticationStatus(period)}
              reloadKey={reloadKey}
            />
          )}
          {tab === 1 && <BusinessRankingSection reloadKey={reloadKey} />}
          {tab === 2 && <ApplicationsSection reloadKey={reloadKey} />}
          {tab === 3 && (
            <DeviceAnalyticsSection fetchDevices={() => adminDashboardService.getDevices('30d')} reloadKey={reloadKey} />
          )}
          {/* {tab === 4 && <SecuritySection reloadKey={reloadKey} />} */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
